import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { Button, ButtonIcon } from '@backstage/ui';
import CloseIcon from '@material-ui/icons/Close';
import { WalkthroughDialogProps } from '../../types';

const useStyles = makeStyles({
  paper: {
    width: '536px',
    maxWidth: '536px',
    borderRadius: '4px',
    boxShadow:
      '0px 0px 4px rgba(0,0,0,0.25), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.12)',
  },
  titleRoot: {
    padding: '8px 12px',
    borderBottom: '1px solid #D9D9D9',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  titleText: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    lineHeight: 1.4,
    color: 'var(--arla-text-primary-color)',
    flex: 1,
  },
  closeButton: {
    padding: '2px',
    color: 'var(--arla-text-primary-color)',
  },
  contentRoot: {
    padding: '8px 12px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    lineHeight: 1.4,
    color: 'var(--arla-text-primary-color)',
  },
  bodyText: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Roboto, sans-serif',
    lineHeight: 1.4,
    color: 'var(--arla-text-primary-color)',
    '& p': {
      margin: 0,
    },
    '& p + p': {
      marginTop: '14px',
    },
  },
  actionsRoot: {
    padding: '0 12px',
    height: '52px',
    borderTop: '1px solid #D9D9D9',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  pageCounter: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    color: '#757575',
    lineHeight: 1.4,
  },
  prevButton: {
    height: '32px',
    minWidth: '71px',
    padding: '6px 8px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    textTransform: 'none',
    lineHeight: 1.4,
    border: '1px solid #E5E5E5',
    color: 'var(--arla-text-primary-color)',
    "&[data-disabled='true']": {
      borderColor: '#CCCCCC',
      color: '#9C9C9C',
    },
  },
  nextButton: {
    height: '32px',
    minWidth: '60px',
    padding: '6px 8px',
    borderRadius: '4px',
    backgroundColor: '#1F5493',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    textTransform: 'none',
    lineHeight: 1.4,
    '&:hover': {
      backgroundColor: '#174077',
    },
  },
});

export const WalkthroughDialog = ({
  dialogTitle,
  storageKey,
  pages,
  triggerPath,
}: WalkthroughDialogProps) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [seenBefore] = useState(() => Boolean(localStorage.getItem(storageKey)));
  const [currentPage, setCurrentPage] = useState(0);

  const isOnPath = !triggerPath || pathname === triggerPath || pathname.startsWith(`${triggerPath}?`);
  const open = isOnPath && !seenBefore && !dismissed;

  useEffect(() => {
    if (!isOnPath) {
      setCurrentPage(0);
    }
  }, [isOnPath]);

  if (pages.length === 0) {
    return null;
  }

  const handleClose = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
  };

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(p => p + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(p => p - 1);
    }
  };

  const page = pages[currentPage];
  const isFirst = currentPage === 0;
  const isLast = currentPage === pages.length - 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
      aria-labelledby="walkthrough-dialog-title"
    >
      <DialogTitle
        id="walkthrough-dialog-title"
        disableTypography
        classes={{ root: classes.titleRoot }}
      >
        <div className={classes.titleContent}>
          <span className={classes.titleText}>{dialogTitle}</span>
          <ButtonIcon
            aria-label="close"
            onClick={handleClose}
            className={classes.closeButton}
            size="small"
            variant="tertiary"
            icon={<CloseIcon style={{ fontSize: '16px' }} />}
          />
        </div>
      </DialogTitle>

      <DialogContent classes={{ root: classes.contentRoot }}>
        <Typography className={classes.sectionTitle}>{page.title}</Typography>
        <div className={classes.bodyText}>{page.content}</div>
      </DialogContent>

      <DialogActions classes={{ root: classes.actionsRoot }}>
        <span style={{ flex: 1 }} />
        <span className={classes.pageCounter}>
          {currentPage + 1}/{pages.length}
        </span>
        <Button
          onClick={handlePrevious}
          isDisabled={isFirst}
          className={classes.prevButton}
        >
          Previous
        </Button>
        <Button onClick={handleNext} className={classes.nextButton}>
          {isLast ? 'Close' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
